import React, { useEffect, useState } from 'react';
import { afterLogin } from '../components/PrivateRoute';
import { Navigate, useNavigate } from 'react-router-dom';
import { useUser, useSignIn } from '@clerk/react';
import { useAuth } from '../contexts/AuthProvider';
import { Button } from '../components/ui/button';
import ReplaiMark from '../components/ReplaiMark';

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" className="mr-2">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const Login = () => {
  const navigate = useNavigate();
  const { isSignedIn, isLoaded } = useUser();
  const { signIn } = useSignIn();
  const { user: appUser, loading: authLoading } = useAuth();
  const [error, setError] = useState('');
  const [oauthLoading, setOauthLoading] = useState(false);

  const handleGoogleSignIn = async () => {
    setOauthLoading(true);
    setError('');
    try {
      await signIn.sso({
        strategy: 'oauth_google',
        redirectCallbackUrl: '/sso-callback',
        redirectUrl: '/dashboard',
      });
    } catch (err) {
      console.error('OAuth error:', err);
      setError('Sign in failed. Please try again.');
      setOauthLoading(false);
    }
  };

  useEffect(() => {
    if (appUser) navigate(afterLogin(), { replace: true });
  }, [appUser, navigate]);

  if (authLoading || !isLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[hsl(var(--primary))]" />
      </div>
    );
  }

  if (appUser) return <Navigate to={afterLogin()} replace />;

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-2">
      <div className="hidden md:flex flex-col justify-between p-12 bg-[hsl(var(--secondary))] border-r border-border/70">
        <div className="flex items-center gap-3">
          <ReplaiMark size={44} />
          <span className="font-display text-2xl font-semibold">Replai</span>
        </div>
        <div className="max-w-md">
          <h2 className="font-display text-5xl font-semibold leading-[1.05] tracking-tight">
            Read&nbsp;the&nbsp;ticket.<br />
            <span className="text-[hsl(var(--primary))]">Draft&nbsp;the&nbsp;reply.</span>
          </h2>
          <p className="mt-6 text-base text-muted-foreground leading-relaxed">
            Replai triages the LampTwist inbox, pulls the data an agent would look up by hand,
            and hands you a draft ready to review.
          </p>
        </div>
        <p className="text-xs text-muted-foreground">© LampTwist · Internal tool</p>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <div className="md:hidden flex items-center gap-3 mb-10">
            <ReplaiMark size={40} />
            <span className="font-display text-2xl font-semibold">Replai</span>
          </div>
          <h1 className="font-display text-3xl font-semibold">Welcome back</h1>
          <p className="mt-2 text-sm text-muted-foreground">Sign in with your LampTwist Google account.</p>

          {error && (
            <div className="mt-6 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {error}
            </div>
          )}

          <Button
            className="w-full mt-8 h-11 rounded-xl"
            variant="outline"
            onClick={handleGoogleSignIn}
            disabled={oauthLoading}
          >
            {oauthLoading ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-foreground mr-2" />
            ) : (
              <GoogleIcon />
            )}
            Continue with Google
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Login;
